import { useRef, useState } from 'react';
import validator from 'validator';
import {
  Button,
  enqueueSnackbar,
  InputContainer,
  SearchableSelect,
  Select,
  Typography,
} from '../../design-system';
import { useLoadedGateOutMutation, useQuery } from '../../hooks';
import { INPUT_ERRORS, MESSAGES, REGEX } from '../../const';
import { DashbaordFormFooter, DashboardInputs, DashboardWrapper } from '../dashboard';
import { gateOutsService } from '../../services';

export const GateOutsLoaded = () => {
  const loadedMutation = useLoadedGateOutMutation();

  const containerId = useRef('');
  const [containerNumber, setContainerNumber] = useState('');
  const [licensePlateNumber, setLicensePlateNumber] = useState('');
  const [authorizedLicensePlateNumbers, setAuthorizedLicensePlateNumbers] = useState([]);

  const [searchBy, setSearchBy] = useState<'container' | 'licensePlateNumber' | undefined>();
  const [updateKey, setUpdateKey] = useState(Math.random());

  const {
    data = { containerOptions: [], clientCode: '', allRequestTotalCount: 0 },
    isFetching: isContainersLoading,
  } = useQuery(
    ['gate-outs', 'loaded-containers', searchBy === 'licensePlateNumber' ? licensePlateNumber : ''],
    () =>
      gateOutsService.options({
        status: 'LOADING_ALLOWED',
        ...(searchBy === 'licensePlateNumber' ? { licensePlateNumber } : {}),
        includeGateOutLoaded: true,
        notLoading: false,
      })(),
  );

  const validators = {
    licensePlateNumber: !String(licensePlateNumber).length
      ? INPUT_ERRORS.REQUIRED.LICENSE_PLATE_NUMBER
      : !validator.isLength(licensePlateNumber, { min: 4, max: 10 })
      ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER_LENGTH
      : !REGEX.LICENSE_PLATE_NUMBER.test(licensePlateNumber)
      ? INPUT_ERRORS.NOT_VALID.LICENSE_PLATE_NUMBER
      : '',
  };

  const resetForm = () => {
    containerId.current = '';
    setContainerNumber('');

    setLicensePlateNumber('');
    setAuthorizedLicensePlateNumbers([]);

    setSearchBy(undefined);
    setUpdateKey(Math.random());
  };

  const handleLicensePlateNumberSelect = (option: any) => {
    option = typeof option === 'string' ? option : '';

    if (option === licensePlateNumber || !option) {
      resetForm();
    } else {
      if (searchBy === 'licensePlateNumber') {
        resetForm();
        setSearchBy('licensePlateNumber');
      }

      setLicensePlateNumber(option);
      if (!searchBy) setSearchBy('licensePlateNumber');
    }
  };

  const handleContainerSelect = (option: any) => {
    if (containerId.current === option.id || !option.id) {
      resetForm();
    } else {
      containerId.current = option.id;
      setContainerNumber(option.value);
      setUpdateKey(Math.random());
    }
  };

  const handleSubmitClick = () => {
    if (Object.values(validators).some((field) => !!field)) {
      return;
    }

    const data: any = {
      driver: {
        licensePlateNumber,
      },
    };

    Object.keys(data.driver).forEach((key) => {
      if (!data.driver[key]) delete data.driver[key];
    });
    if (!Object.keys(data.driver).length) delete data.driver;

    loadedMutation.mutate(
      {
        id: containerId.current,
        props: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(MESSAGES.GATE_OUT.SUCCESS.LOADED, 'success');
          resetForm();
        },
        onError: () => enqueueSnackbar(MESSAGES.GATE_OUT.ERROR.NOT_LOADED, 'error'),
      },
    );
  };

  return (
    <>
      <DashboardWrapper>
        <Typography variant={'textXL'} weight={'bold'}>
          Gate Out Loaded
        </Typography>
        <DashboardInputs key={updateKey}>
          <Typography variant={'textM'} weight={'bold'}>
            Total Requests: {data.allRequestTotalCount}
          </Typography>
          {!!data.clientCode && (
            <Typography variant={'textM'} weight={'bold'}>
              Client Code: {data.clientCode}
            </Typography>
          )}
          {searchBy !== 'container' && (
            <SearchableSelect
              selectedOptionLabel={licensePlateNumber || 'Select License Plate Number'}
              label={'Allowed License Plate Numbers*'}
              onSelect={handleLicensePlateNumberSelect}
              queryFn={(inputValue) =>
                gateOutsService.getLicensePlateNumber({
                  params: {
                    licensePlateNumber: inputValue,
                    status: 'LOADING_ALLOWED',
                    includeGateOutLoaded: true,
                  },
                })
              }
              queryKey={['gate-outs', 'licensePlateNumber']}
              withClearButton={!!licensePlateNumber}
            />
          )}
          <SearchableSelect
            InputComponent={InputContainer}
            label={'Container Number*'}
            selectedOptionLabel={containerNumber || 'Select Container Number'}
            options={data.containerOptions.map((option: any) => ({
              ...option,
              value: option.label,
            }))}
            onSelect={handleContainerSelect}
            selectedAreasEqaul={{ key: 'id', keys: [containerId.current] }}
            withClearButton={!!containerNumber}
            isLoading={isContainersLoading}
            disabled={!licensePlateNumber}
          />
          {!!containerId.current && (
            <>
              {searchBy === 'container' && (
                <Select
                  label={'Allowed License Plate Numbers*'}
                  options={authorizedLicensePlateNumbers}
                  value={licensePlateNumber}
                  onSelect={handleLicensePlateNumberSelect}
                  withClearButton={!!licensePlateNumber}
                />
              )}
            </>
          )}
        </DashboardInputs>
        <DashbaordFormFooter>
          <Button size={'medium'} onClick={handleSubmitClick} disabled={!containerId.current}>
            Submit
          </Button>
        </DashbaordFormFooter>
      </DashboardWrapper>
    </>
  );
};
